import { useState, useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { t } from 'ttag';
import queryString from 'query-string';

import { memberSettingsSafePaymentMethods } from '@urls';
import {
  ShopSettingsDeliveryMethod,
  ShopSettingsDeliveryMethodProps,
  ChangedData,
} from '@components/settings/ShopSettingsDeliveryMethod';
import LastEnabledDeliveryServiceModal from '@components/settings/LastEnabledDeliveryServiceModal';
import { notifySuccess } from '@utils/notifications';
import { DeliveryService } from '@newModels';
import { VerifyRozetkaEnableModal } from '@components/settings/VerifyRozetkaEnableModal';
import { AddPaymentCardForRozetkaDeliveryModal } from '@components/settings/AddPaymentCardForRozetkaDeliveryModal';
import {
  ShopSettingsDeliveryMethodsDocument,
  ShopSettingsDeliveryMethodsQuery,
} from '@containers/settings/ShopSettingsDeliveryMethods/graphql.v4.generated';

import {
  useShopSettingsDeliveryMethodMutation,
} from './gql/graphql.v4.generated';

// types
type ShopSettingsDeliveryMethodContainerProps = Omit<ShopSettingsDeliveryMethodProps, 'onChange'> & {
  isSafePaymentEnabled: boolean,
  isPaymentCardAdded: boolean,
};

// constants
const successSaveNotifyId = 'delivery_settings_success_save';
const forceRozetkaEnableSearchKey = 'forceRozetkaEnable';

export const ShopSettingsDeliveryMethodContainer:
React.FC<ShopSettingsDeliveryMethodContainerProps> = ({
  isSafePaymentEnabled,
  isPaymentCardAdded,
  ...props
}) => {
  const client = useApolloClient();
  const history = useHistory();
  const location = useLocation();
  const { deliveryService } = props;
  const [isLastServiceModalOpen, setIsLastServiceModalOpen] = useState(false);
  const [rozetkaEnableModalData, setRozetkaEnableModalData] = useState<ChangedData>();
  const [isAddPaymentCardModalOpen, setIsAddPaymentCardModalOpen] = useState(false);

  const searchParams = queryString.parse(location.search);
  const isRozetka = deliveryService === DeliveryService.Rozetka;
  const isForceEnableRozetka = searchParams[forceRozetkaEnableSearchKey];

  const [updateDeliveryMethod] = useShopSettingsDeliveryMethodMutation({
    context: { v4: true },
    refetchQueries: [
      ShopSettingsDeliveryMethodsDocument,
    ],
  });

  const isLastEnabledDeliveryService = useCallback(() => {
    const data = client.readQuery<ShopSettingsDeliveryMethodsQuery>({
      query: ShopSettingsDeliveryMethodsDocument,
    });

    if (data?.viewer) {
      for (const deliverySetting of data.viewer.deliverySettings) {
        if (deliverySetting.isEnabled && deliverySetting.deliveryService !== deliveryService) {
          return false;
        }
      }
    }

    return true;
  }, [client, deliveryService]);

  const handleChange = useCallback(async (deliveryServiceData: ChangedData, verify = false) => {
    if (!deliveryServiceData.isEnabled && isLastEnabledDeliveryService()) {
      setIsLastServiceModalOpen(true);
      return;
    }

    if (isRozetka && deliveryServiceData.isEnabled) {
      // Verify turning on Rozetka when no added payment cards
      if (!isPaymentCardAdded) {
        setIsAddPaymentCardModalOpen(true);
        return;
      }

      // Verify turning on Rozetka when Safe Payment is disabled
      if (!isSafePaymentEnabled && !verify) {
        setRozetkaEnableModalData(deliveryServiceData);
        return;
      }
    }

    const optimisticResponse = {
      userDeliverySettingUpdate: {
        deliverySetting: {
          deliveryService,
          isEnabled: deliveryServiceData.isEnabled,
          region:
            deliveryServiceData.region
              ? {
                ...deliveryServiceData.region,
                __typename: 'OrderRegion' as const,
              }
              : null,
          city:
            deliveryServiceData.city
              ? {
                ...deliveryServiceData.city,
                __typename: 'OrderCity' as const,
              }
              : null,
          warehouse:
            deliveryServiceData.warehouse
              ? {
                ...deliveryServiceData.warehouse,
                __typename: 'DeliverySettingsWarehouse' as const,
              }
              : null,
          __typename: 'UserDeliverySettings' as const,
        },
        errors: null,
        __typename: 'deliverySettingUpdateResponse' as const,
      },
    };

    await updateDeliveryMethod({
      optimisticResponse: () => optimisticResponse,
      variables: {
        deliveryService,
        ...deliveryServiceData,
      },
    });

    setRozetkaEnableModalData(undefined);

    notifySuccess(
      t`Зміни почнуть діяти для всіх товарів`,
      { toastId: successSaveNotifyId },
    );
  }, [
    deliveryService,
    isLastEnabledDeliveryService,
    isPaymentCardAdded,
    isRozetka,
    isSafePaymentEnabled,
    updateDeliveryMethod,
  ]);

  const handleAddPaymentCard = () => {
    history.push({
      pathname: memberSettingsSafePaymentMethods,
      search: queryString.stringify({
        next: `${location.pathname}?${forceRozetkaEnableSearchKey}=true`,
      }),
    });
  };

  useEffect(() => {
    const handleForseEnableRozetka = async () => {
      await handleChange({
        isEnabled: true,
      });

      delete searchParams[forceRozetkaEnableSearchKey];

      history.replace({
        pathname: location.pathname,
        search: queryString.stringify({
          ...searchParams,
        }),
      });
    };

    if (isRozetka && isForceEnableRozetka) {
      handleForseEnableRozetka();
    }
  }, [
    handleChange,
    history,
    isForceEnableRozetka,
    isRozetka,
    location.pathname,
    searchParams,
  ]);

  return (
    <>
      <ShopSettingsDeliveryMethod
        onChange={handleChange}
        {...props}
      />

      <LastEnabledDeliveryServiceModal
        isOpen={isLastServiceModalOpen}
        onRequestClose={() => setIsLastServiceModalOpen(false)}
      />

      <VerifyRozetkaEnableModal
        isOpen={!!rozetkaEnableModalData}
        onRequestClose={() => setRozetkaEnableModalData(undefined)}
        onConfirm={() => handleChange(rozetkaEnableModalData!, true)}
      />

      <AddPaymentCardForRozetkaDeliveryModal
        isOpen={isAddPaymentCardModalOpen}
        onConfirm={handleAddPaymentCard}
        onRequestClose={() => setIsAddPaymentCardModalOpen(false)}
      />
    </>
  );
};
